import { useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import { trim } from "lodash";
import PropTypes from "prop-types";
import * as creators from "../actions/creators";
import * as api from "../actions/api";
import { updateAccountCountry } from "../../reducers/layoutReducer/layoutReducer";
import getProvinceObject from "../helpers";
import Dropdown from "../../shared/v2/Dropdown";
import TextInputWrap from "./TextInputWrap";
import NewOfficeModal from "./NewOfficeModal";
import OfficeRemovalModal from "./OfficeRemovalModal";
import OfficeCard, { OFFICE_PROPS } from "./OfficeCard";
import { useAddressModal, useAddressModalCallback } from "@shared/AddressModal";
import { getStandardizedAddressApi, getSuggestedAddressCheck } from "../services";

const parseCoordinate = (str) => {
  if (typeof str !== "string") return null;

  const normalized = str.startsWith("_") ? "-" + str.slice(1) : str;

  const num = Number(normalized);
  return Number.isNaN(num) ? null : num;
};

const GET_SECTIONS = (states, territories) => [
  { title: "Phone", key: "phone", editable: true, comp: TextInputWrap },
  { title: "Fax", key: "fax", editable: true, comp: TextInputWrap },
  {
    title: "Street Address",
    key: "street_address",
    editable: true,
    comp: TextInputWrap,
    isAddress: true,
  },
  { title: "City", key: "city", editable: true, comp: TextInputWrap, isAddress: true },
  {
    title: "State / Province",
    key: "locality",
    editable: true,
    isDD: true,
    comp: ({ value, onBlur, onChange, onKeyPress }) => (
      <Dropdown
        containerClassName="tw-flex-1"
        menuShouldComeToFront
        isSearchable
        isClearable
        placeholder="Select"
        value={getProvinceObject(states, territories, value)}
        onChange={(v) => onChange(v?.value)}
        onBlur={onBlur}
        onKeyDown={onKeyPress}
        options={[
          {
            label: "States",
            options: states,
          },
          {
            label: "Province",
            options: territories,
          },
        ]}
      />
    ),
    isAddress: true,
    displayValue: (value) => getProvinceObject(states, territories, value)?.label,
  },
  { title: "ZIP / Postal Code", key: "postal_code", editable: true, comp: TextInputWrap, isAddress: true },
  { title: "Country", key: "country", editable: true, comp: TextInputWrap, isAddress: true },
];

const OfficeCards = ({ model: modl, setValue, saveValue, states, territories }) => {
  const dispatch = useDispatch();
  const [model, setModel] = useState(modl);
  const SECTIONS = GET_SECTIONS(states, territories);
  const addressModal = useAddressModal("addEdit");
  const invalidModal = useAddressModal("invalid");
  const verifyModal = useAddressModal("verify");
  const { setOnSubmit, setInvalidOnSubmit, setInvalidOnEdit, setVerifyOnSubmit, setVerifyOnEdit } =
    useAddressModalCallback();

  const [showOfficeModal, setShowOfficeModal] = useState(false);
  const [officeToRemove, setOfficeToRemove] = useState(null);
  const removeOffice = (id) => {
    setValue("account_offices", [...model.account_offices.filter((o) => o.id !== id)]);
    saveValue("account_offices_attributes", [{ id, _destroy: true }]);
  };

  useEffect(() => {
    const fetchStandardizedAddresses = async () => {
      const { res } = await getStandardizedAddressApi();

      if (res) {
        const standardizedAddresses = res.data.standardizedAddresses;
        const newModel = { ...modl };

        // Set lat/long for main address
        if (newModel.address?.id && standardizedAddresses[newModel.address.id]) {
          const standardized = standardizedAddresses[newModel.address.id];
          newModel.address = {
            ...newModel.address,
            lat: standardized.lat ? parseCoordinate(standardized.lat) : null,
            long: standardized.long ? parseCoordinate(standardized.long) : null,
          };
        }

        // Set lat/long for additional office addresses
        if (newModel.account_offices?.length) {
          newModel.account_offices = newModel.account_offices.map((office) => {
            if (office.address?.id && standardizedAddresses[office.address.id]) {
              const standardized = standardizedAddresses[office.address.id];
              return {
                ...office,
                address: {
                  ...office.address,
                  lat: standardized.lat ? parseCoordinate(standardized.lat) : null,
                  long: standardized.long ? parseCoordinate(standardized.long) : null,
                },
              };
            }
            return office;
          });
        }

        setModel(newModel);
      }
    };

    fetchStandardizedAddresses();
  }, [modl]);

  const mapModelToOffice = (m) => ({
    id: m.id,
    name: m.name,
    phone: m.phone,
    fax: m.fax,
    street_address: m.address?.street_address,
    secondary_address: m.address?.secondary_address,
    city: m.address?.city,
    locality: m.address?.locality,
    postal_code: m.address?.postal_code,
    country: m.address?.country,
    lat: m.address?.lat,
    long: m.address?.long,
  });

  const createOfficeAddressSubmit = (office, officeIdx) => async (address) => {
    const { streetAddress, secondaryAddress, city, locality, postalCode, country } = address;
    const { res } = await getSuggestedAddressCheck({
      city,
      postalCode,
      streetAddress,
      secondaryAddress,
      locality,
      country,
    });
    addressModal.close();

    const updateOfficeAddress = (addressData) => {
      const offices = [...model.account_offices];
      offices[officeIdx] = {
        ...offices[officeIdx],
        address: {
          ...offices[officeIdx].address,
          ...addressData,
        },
      };
      setValue("account_offices", offices);
      saveValue("account_offices_attributes", [{ id: office.id, address_attributes: addressData }]);
    };

    if (!res.data?.addressDetails?.standardized) {
      if (trim(res.data?.addressDetails?.streetAddress) === "") {
        setInvalidOnSubmit(() => {
          updateOfficeAddress({
            city,
            postal_code: postalCode,
            street_address: streetAddress,
            secondary_address: secondaryAddress,
            locality,
            country,
          });
        });
        setInvalidOnEdit(() => {
          setOnSubmit(createOfficeAddressSubmit(office, officeIdx));
          addressModal.open({
            title: "Edit Address",
            initAddress: {
              city,
              postalCode,
              streetAddress,
              secondaryAddress,
              locality,
              country,
            },
            hideAddressType: true,
          });
          invalidModal.close();
        });
        invalidModal.open({
          address: {
            city,
            postalCode,
            streetAddress,
            secondaryAddress,
            locality,
            country,
          },
        });
      } else {
        setVerifyOnSubmit((ad) => {
          updateOfficeAddress({
            city,
            postal_code: ad.postalCode,
            street_address: ad.streetAddress,
            secondary_address: ad.secondaryAddress,
            locality: ad.locality,
            country: ad.country,
          });
        });
        setVerifyOnEdit(() => {
          setOnSubmit(createOfficeAddressSubmit(office, officeIdx));
          addressModal.open({
            title: "Edit Address",
            initAddress: {
              city,
              postalCode,
              streetAddress,
              secondaryAddress,
              locality,
              country,
            },
            hideAddressType: true,
          });
          verifyModal.close();
        });
        verifyModal.open({
          originalAddress: {
            city,
            postalCode,
            streetAddress,
            secondaryAddress,
            locality,
            country,
          },
          suggestedAddress: {
            city: res.data?.addressDetails?.city,
            postalCode: res.data?.addressDetails?.postalCode,
            streetAddress: res.data?.addressDetails?.streetAddress,
            secondaryAddress: res.data?.addressDetails?.secondaryAddress,
            locality: res.data?.addressDetails?.locality,
            country: res.data?.addressDetails?.country,
          },
        });
      }
    } else {
      updateOfficeAddress({
        city,
        postal_code: postalCode,
        street_address: streetAddress,
        secondary_address: secondaryAddress,
        locality,
        country,
      });
    }
  };

  const onAddEditAddressSubmit = async (address) => {
    const { streetAddress, secondaryAddress, city, locality, postalCode, country } = address;
    const { res } = await getSuggestedAddressCheck({
      city,
      postalCode,
      streetAddress,
      secondaryAddress,
      locality,
      country,
    });
    addressModal.close();

    if (!res.data?.addressDetails?.standardized) {
      if (trim(res.data?.addressDetails?.streetAddress) === "") {
        setInvalidOnSubmit(() => {
          setValue("address", {
            city,
            postal_code: postalCode,
            street_address: streetAddress,
            secondary_address: secondaryAddress,
            locality,
            country,
          });
          saveValue("address_attributes", {
            city,
            postal_code: postalCode,
            street_address: streetAddress,
            secondary_address: secondaryAddress,
            locality,
            country,
          });
        });
        setInvalidOnEdit(() => {
          setOnSubmit(onAddEditAddressSubmit);
          addressModal.open({
            title: "Edit Address",
            initAddress: {
              city,
              postalCode,
              streetAddress,
              secondaryAddress,
              locality,
              country,
            },
            hideAddressType: true,
          });
          invalidModal.close();
        });
        invalidModal.open({
          address: {
            city,
            postalCode,
            streetAddress,
            secondaryAddress,
            locality,
            country,
          },
        });
      } else {
        setVerifyOnSubmit((ad) => {
          setValue("address", {
            city,
            postal_code: ad.postalCode,
            street_address: ad.streetAddress,
            secondary_address: ad.secondaryAddress,
            locality: ad.locality,
            country: ad.country,
          });
          saveValue("address_attributes", {
            city,
            postal_code: ad.postalCode,
            street_address: ad.streetAddress,
            secondary_address: ad.secondaryAddress,
            locality: ad.locality,
            country: ad.country,
          });
        });
        setVerifyOnEdit(() => {
          setOnSubmit(onAddEditAddressSubmit);
          addressModal.open({
            title: "Edit Address",
            initAddress: {
              city,
              postalCode,
              streetAddress,
              secondaryAddress,
              locality,
              country,
            },
            hideAddressType: true,
          });
          verifyModal.close();
        });
        verifyModal.open({
          originalAddress: {
            city,
            postalCode,
            streetAddress,
            secondaryAddress,
            locality,
            country,
          },
          suggestedAddress: {
            city: res.data?.addressDetails?.city,
            postalCode: res.data?.addressDetails?.postalCode,
            streetAddress: res.data?.addressDetails?.streetAddress,
            secondaryAddress: res.data?.addressDetails?.secondaryAddress,
            locality: res.data?.addressDetails?.locality,
            country: res.data?.addressDetails?.country,
          },
        });
      }
    } else {
      setValue("address", {
        city,
        postal_code: postalCode,
        street_address: streetAddress,
        secondary_address: secondaryAddress,
        locality,
        country,
      });
      saveValue("address_attributes", {
        city,
        postal_code: postalCode,
        street_address: streetAddress,
        secondary_address: secondaryAddress,
        locality,
        country,
      });
      dispatch(updateAccountCountry(country));
    }
  };

  return (
    <>
      <OfficeCard
        model={mapModelToOffice(model)}
        SECTIONS={SECTIONS.filter((s) => !s.isAddress)}
        onEditAddress={(newAddress) => {
          setOnSubmit(onAddEditAddressSubmit);
          addressModal.open({
            title: newAddress ? "Add Address" : "Edit Address",
            initAddress: {
              streetAddress: mapModelToOffice(model).street_address,
              secondaryAddress: mapModelToOffice(model).secondary_address,
              city: mapModelToOffice(model).city,
              locality: mapModelToOffice(model).locality,
              postalCode: mapModelToOffice(model).postal_code,
              country: mapModelToOffice(model).country,
            },
            hideAddressType: true,
          });
        }}
        setValue={(key, val) => {
          let m = {};
          const isAddress = SECTIONS.find((s) => s.key === key)?.isAddress;
          if (isAddress) {
            m = { ...model.address };
            m[key] = val;
          } else {
            m = val;
          }
          setValue(isAddress ? "address" : key, m);
        }}
        saveValue={(key, val) => {
          let m = {};
          const isAddress = SECTIONS.find((s) => s.key === key)?.isAddress;
          if (isAddress) {
            m[key] = val;
          } else {
            m = val;
          }
          saveValue(isAddress ? "address_attributes" : key, m);
        }}
        onAdd={model?.account_offices.length === 0 ? setShowOfficeModal : null}
      />
      {model?.account_offices?.map((office, officeIdx) => (
        <OfficeCard
          key={office.id}
          SECTIONS={SECTIONS.filter((s) => !s.isAddress)}
          editable
          model={mapModelToOffice(office)}
          onEditAddress={() => {
            setOnSubmit(createOfficeAddressSubmit(office, officeIdx));
            addressModal.open({
              title: "Edit Address",
              initAddress: {
                streetAddress: mapModelToOffice(office).street_address,
                secondaryAddress: mapModelToOffice(office).secondary_address,
                city: mapModelToOffice(office).city,
                locality: mapModelToOffice(office).locality,
                postalCode: mapModelToOffice(office).postal_code,
                country: mapModelToOffice(office).country,
              },
              hideAddressType: true,
            });
          }}
          setValue={(key, val) => {
            const offices = [...model?.account_offices];
            const m = { ...offices[officeIdx] };
            const isAddress = SECTIONS.find((s) => s.key === key)?.isAddress;
            if (isAddress) {
              m.address = { ...office.address };
              m.address[key] = val;
            } else {
              m[key] = val;
            }
            offices[officeIdx] = m;
            setValue("account_offices", offices);
          }}
          saveValue={(key, val) => {
            const m = { id: office.id };
            const isAddress = SECTIONS.find((s) => s.key === key)?.isAddress;
            if (isAddress) {
              m.address_attributes = {};
              m.address_attributes[key] = val;
            } else {
              m[key] = val;
            }
            saveValue("account_offices_attributes", [m]);
          }}
          onAdd={model?.account_offices.length === officeIdx + 1 ? setShowOfficeModal : null}
          onRemove={setOfficeToRemove}
        />
      ))}

      <OfficeRemovalModal
        officeToRemove={officeToRemove}
        onRemove={() => {
          removeOffice(officeToRemove?.id);
          setOfficeToRemove();
        }}
        onClose={() => {
          setOfficeToRemove();
        }}
      />

      <NewOfficeModal
        open={showOfficeModal}
        onClose={() => setShowOfficeModal(false)}
        setShowOfficeModal={setShowOfficeModal}
      />
    </>
  );
};

OfficeCards.propTypes = {
  ...OFFICE_PROPS,
  territories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  states: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

const mapStateToProps = (state) => ({
  model: state.businessSettings.model,
  states: state.businessSettings.states,
  territories: state.businessSettings.territories,
});

export default connect(mapStateToProps, { ...creators, ...api })(OfficeCards);
